import { Injectable } from '@angular/core';
import { SensorMarker } from './../models/sensor-marker';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { SensorDataService } from './sensor-data.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { isEqual } from 'lodash';
import { AuthService } from '@app/shared/services/auth.service';
import { SensorDataHelperService } from './sensor-data-helper.service';
import { LeafletMouseEvent } from 'leaflet';
import { tap } from 'rxjs/operators';
import { SensorFilter } from '../models/sensor-filter';
import { TransformedNode } from '../models/sensors-by-municipality-nodes';

@Injectable({
  providedIn: 'root',
})
export class SensorMapMarkersService {
  markers: SensorMarker[] = [];
  markerAssetFile = '/assets/icons/ico_iot.png';
  cluster?: L.MarkerClusterGroup;

  public isVisible = new BehaviorSubject<boolean>(false);
  public isDescriptionVisible = new BehaviorSubject<boolean>(false);
  public selectedMarkers: any[] = [];
  public returnRoute: string = '';
  public activeSensor: any;

  constructor(
    private mapStoreService: MapStoreService,
    private sensorDataService: SensorDataService,
    private helper: SensorDataHelperService,
    public authService: AuthService
  ) {
    this.helper.translateText();
  }

  public load(municipalityGuid: string): Observable<SensorFilter<TransformedNode[]> | null> {
    if (!this.authService.permissions.SENSOR_DATA) {
      return of(null);
    }

    this.markers = [];
    this.mapStoreService.clearSensorMarkers();
    this.cluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      iconCreateFunction: (m) =>
        L.divIcon({
          className: 'custom-div-icon',
          html: `<div class='sensor-icon'><img alt="marker-img" src=".${
            this.markerAssetFile
          }"/><div class='sensor-icon-badge'>${m.getChildCount()}</div></div>`,
          iconSize: [48, 48],
          iconAnchor: [24, 24],
        }),
    });

    return this.sensorDataService.getSensorsByMunicipality(municipalityGuid).pipe(
      tap((res: SensorFilter<TransformedNode[]>) => {
        res.data.sensorFilter.nodes.forEach((node: TransformedNode) => {
          if (node.location.latitude && node.location.longitude) {
            this.add(node);
          }
        });

        if (this.isVisible.getValue()) {
          this.show();
        } else {
          this.hide();
        }
      })
    );
  }

  public toggle(): void {
    if (this.isVisible.getValue()) {
      this.hide();
    } else {
      this.show();
    }
  }

  public showIoTMarkers(): void {
    this.show();
  }

  private add(sensor: TransformedNode): void {
    const marker: SensorMarker = new SensorMarker(
      sensor.sensor_id,
      sensor.sensor_type,
      sensor.device_id,
      sensor.device_name,
      sensor.device_eui,
      this.helper.getAddress(sensor.location),
      [sensor.location.latitude, sensor.location.longitude],
      sensor.buildingID,
      sensor.locationContext,
      sensor.alarmRuleEnabled,
      sensor.currentQuality,
      sensor.ruleJSON,
      sensor.reading,
      {
        icon: L.icon({ iconUrl: this.markerAssetFile, iconAnchor: [24, 24], iconSize: [48, 48] }),
      }
    ).addTo(this.cluster!);
    this.markers.push(marker);

    this.addMarkerEvent(marker);

    this.mapStoreService.addSensorMarker(marker);
  }

  private show(): void {
    (this.mapStoreService.map as L.Map).addLayer(this.cluster!);
    this.isVisible.next(true);
  }

  private hide(): void {
    (this.mapStoreService.map as L.Map).removeLayer(this.cluster!);
    this.isVisible.next(false);
  }

  private addMarkerEvent(cluster: any): void {
    cluster.on('click', (e: LeafletMouseEvent) => {
      this.selectedMarkers = [e.target];
      this.isDescriptionVisible.next(!this.isDescriptionVisible.value);
    });

    this.cluster?.on('clusterclick', (e: any) => {
      if ((this.mapStoreService.map as L.Map).getZoom() === (this.mapStoreService.map as L.Map).getMaxZoom()) {
        const modifiedMarker: any[] = [];
        modifiedMarker.push(e.layer.getAllChildMarkers());

        if (this.isDescriptionVisible && isEqual(modifiedMarker, this.selectedMarkers)) {
          this.isDescriptionVisible.next(!this.isDescriptionVisible.value);
          return;
        }
        this.selectedMarkers = modifiedMarker;
        this.isDescriptionVisible.next(!this.isDescriptionVisible.value);
      }
    });
  }
}
